// ═══════════════════════════════════════════════════════════════
// BillingConfig.js — Plans, prices, limits, feature matrix
// Reusable billing module configuration. Host app overrides via mergeConfig().
// ═══════════════════════════════════════════════════════════════

// ── Plan Limits (enforcement) ────────────────────────────────

const PLAN_LIMITS = {
  personal:       { maxConflicts: 3,        mentorAccess: false, minSeats: 1,  maxSeats: 1   },
  'personal-plus': { maxConflicts: Infinity, mentorAccess: true,  minSeats: 1,  maxSeats: 1   },
  professional:   { maxConflicts: Infinity, mentorAccess: true,  minSeats: 5,  maxSeats: 100 },
  enterprise:     { maxConflicts: Infinity, mentorAccess: true,  minSeats: 25, maxSeats: 999 },
};

function getPlanLimits(planName) {
  return PLAN_LIMITS[planName] || null;
}

// ── Plan Definitions (UI) ────────────────────────────────────

const PERSONAL_PLANS = [
  {
    key: 'personal',
    name: 'Personal',
    tagline: 'For individuals getting started',
    monthlyPrice: 9.99,
    annualMonthlyPrice: 7.99,
    annualTotalPrice: 95.88,
    priceKeyMonthly: 'personal_monthly',
    priceKeyAnnual: 'personal_annual',
    category: 'personal',
    features: [
      { label: 'Up to 3 active conflicts', included: true },
      { label: 'Structured 5-phase process', included: true },
      { label: 'AI-assisted communication', included: true },
      { label: 'Conflict export & download', included: true },
      { label: 'Mentor panel access', included: false },
      { label: 'Priority support', included: false },
    ],
  },
  {
    key: 'personal-plus',
    name: 'Personal+',
    tagline: 'For professionals & power users',
    monthlyPrice: 19.99,
    annualMonthlyPrice: 15.99,
    annualTotalPrice: 191.88,
    priceKeyMonthly: 'personal_plus_monthly',
    priceKeyAnnual: 'personal_plus_annual',
    category: 'personal',
    popular: true,
    features: [
      { label: 'Unlimited active conflicts', included: true },
      { label: 'Structured 5-phase process', included: true },
      { label: 'AI-assisted communication', included: true },
      { label: 'Conflict export & download', included: true },
      { label: 'Mentor panel access', included: true },
      { label: 'Priority support', included: true },
    ],
  },
];

const ORG_PLANS = [
  {
    key: 'professional',
    name: 'Professional',
    tagline: 'For teams & organizations',
    monthlyPrice: 12.00,
    annualMonthlyPrice: 10.00,
    annualTotalPrice: 120.00,
    priceKeyMonthly: 'professional_monthly',
    priceKeyAnnual: 'professional_annual',
    category: 'org',
    perSeat: true,
    minSeats: 5,
    features: [
      { label: 'Unlimited active conflicts', included: true },
      { label: 'All roles & admin dashboard', included: true },
      { label: 'AI-assisted communication', included: true },
      { label: 'Mentor panel access', included: true },
      { label: 'NDA & confidentiality features', included: true },
      { label: 'Custom branding', included: false },
      { label: 'Dedicated support & SLA', included: false },
    ],
  },
  {
    key: 'enterprise',
    name: 'Enterprise',
    tagline: 'For large organizations',
    monthlyPrice: 18.00,
    annualMonthlyPrice: 15.00,
    annualTotalPrice: 180.00,
    priceKeyMonthly: 'enterprise_monthly',
    priceKeyAnnual: 'enterprise_annual',
    category: 'org',
    perSeat: true,
    minSeats: 25,
    features: [
      { label: 'Unlimited active conflicts', included: true },
      { label: 'All roles & admin dashboard', included: true },
      { label: 'AI-assisted communication', included: true },
      { label: 'Mentor panel access', included: true },
      { label: 'NDA & confidentiality features', included: true },
      { label: 'Custom branding', included: true },
      { label: 'Dedicated support & SLA', included: true },
    ],
  },
];

// ── Stripe Price ID Mapping ──────────────────────────────────

const STRIPE_PRICE_IDS = {
  personal_monthly: null,       // resolved at runtime via ensureStripeProducts
  personal_annual: null,
  personal_plus_monthly: null,
  personal_plus_annual: null,
  professional_monthly: 'price_1TAcr8PBDh5ClUAjPDCGZpLh',
  professional_annual: 'price_1TAcr9PBDh5ClUAjZHj63XEY',
  enterprise_monthly: 'price_1TAcr9PBDh5ClUAjRlTQQppi',
  enterprise_annual: 'price_1TAcrAPBDh5ClUAjzYE4SouB',
};

// ── Default Config ───────────────────────────────────────────

const DEFAULT_CONFIG = {
  trialDays: 14,
  allowPromotionCodes: true,
  gracePeriodDays: 90,
  refundPolicy: 'No refunds on monthly plans. Annual plans receive prorated credit on cancellation.',
  annualSavingsPercent: 20,
};

function mergeConfig(overrides) {
  return { ...DEFAULT_CONFIG, ...overrides };
}

export {
  PLAN_LIMITS,
  getPlanLimits,
  PERSONAL_PLANS,
  ORG_PLANS,
  STRIPE_PRICE_IDS,
  DEFAULT_CONFIG,
  mergeConfig,
};
